import React, {useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {StepperV2} from 'ui-library/stepper/Stepper';
import {JSONValue} from 'util/Types';

import RightSidebarSkeleton, {RightSidebarWidth} from '../components/RightSidebarSkeleton';
import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../RightSidebarTypes';
import {
  buildLookUpAddStepParams,
  getLookUpFormCompleteness,
  getLookUpStepperSteps,
} from './LookUpFormComponents';

export default function LookUpSourceRightSidebar({
  analysis,
  onChangeSelectedRightSidebarItem,
  clientStateId,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.LOOK_UP_SOURCE>): JSX.Element {
  const [stepInput, setStepInput] = useState<PipelineViewGraphQL | null>(null);
  const [stepConfig, setStepConfig] = useState<Record<string, JSONValue> | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const {isComplete, errors} = getLookUpFormCompleteness(stepConfig, stepInput, analysis.steps);
  const canFinalize = isComplete && errors.length === 0 && stepInput != null;

  const finalize = async (): Promise<void> => {
    if (!canFinalize || stepInput == null || stepConfig == null) {
      return;
    }
    try {
      setIsFinalizing(true);
      await AnalysisActions.addOrReplaceAndRunAnalysisStep(
        analysis.id,
        buildLookUpAddStepParams(stepInput, stepConfig, analysis.steps)
      );
      onChangeSelectedRightSidebarItem(null);
    } finally {
      setIsFinalizing(false);
    }
  };

  return (
    <RightSidebarSkeleton
      title="Look up"
      confirmationButtons={[
        {
          text: 'Cancel',
          onClick: () => {
            onChangeSelectedRightSidebarItem(null);
          },
          dataTestAttrsElement: 'cancel',
        },
        {
          text: 'Add table',
          onClick: finalize,
          dataTestAttrsElement: 'add-table',
          disabled: !canFinalize || isFinalizing,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.WIDE}
      dataTestAttrs={{
        component: 'LookUpSourceRightSidebar',
        element: 'root',
      }}
    >
      <StepperV2
        steps={getLookUpStepperSteps({
          analysis,
          stepInput,
          onSelectStepInput: setStepInput,
          stepConfig,
          setStepConfig,
          clientStateId,
        })}
      />
    </RightSidebarSkeleton>
  );
}
